import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface HeaderProps {
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({ darkMode, setDarkMode }) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // set initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const servicesCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const marketsCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openServices = useCallback(() => {
    if (servicesCloseTimer.current) clearTimeout(servicesCloseTimer.current);
    setServicesDropdownOpen(true);
  }, []);

  const closeServices = useCallback(() => {
    servicesCloseTimer.current = setTimeout(() => setServicesDropdownOpen(false), 150);
  }, []);

  const openMarkets = useCallback(() => {
    if (marketsCloseTimer.current) clearTimeout(marketsCloseTimer.current);
    setMarketsDropdownOpen(true);
  }, []);

  const closeMarkets = useCallback(() => {
    marketsCloseTimer.current = setTimeout(() => setMarketsDropdownOpen(false), 150);
  }, []);

  return (
    <header className={`bg-white dark:bg-neutral-dark-100 fixed w-full top-0 z-50 transition-shadow duration-300 ${scrolled ? 'shadow-lg' : 'shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            {isHomePage ? (
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-2xl md:text-3xl font-logo font-bold text-primary-indigo dark:text-white hover:text-primary-rose transition-colors duration-300 cursor-pointer bg-transparent border-0 p-0"
                aria-label="Scroll to top"
              >
                <span className="font-bold">SMPL</span>
                <span className="font-normal ml-1">Digital</span>
              </button>
            ) : (
              <Link
                to="/"
                className="text-2xl md:text-3xl font-logo font-bold text-primary-indigo dark:text-white hover:text-primary-rose transition-colors duration-300"
              >
                <span className="font-bold">SMPL</span>
                <span className="font-normal ml-1">Digital</span>
              </Link>
            )}
          </div>

          {/* Right side - Navigation and Action buttons combined */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8 items-center mr-4">
              {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={openServices}
              onMouseLeave={closeServices}
            >
              <Link
                to="/services"
                className="text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors duration-200 flex items-center"
                aria-haspopup="true"
                aria-expanded={servicesDropdownOpen}
                onFocus={openServices}
                onBlur={closeServices}
              >
                Services
                <svg className="w-4 h-4 ml-1" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Link>
              {servicesDropdownOpen && (
                <div className="absolute top-full left-0 mt-0 pt-2 w-64 dropdown-enter" role="menu" aria-label="Services">
                  <div className="bg-white dark:bg-neutral-dark-200 rounded-lg shadow-xl border border-neutral-200 dark:border-neutral-dark-400 py-2">
                  <Link
                    to="/services/seo"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openServices}
                    onBlur={closeServices}
                  >
                    Search Engine Optimisation
                  </Link>
                  <Link
                    to="/services/website-development"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openServices}
                    onBlur={closeServices}
                  >
                    Website Development
                  </Link>
                  <Link
                    to="/services/ai-automation"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openServices}
                    onBlur={closeServices}
                  >
                    AI & Automation Solutions
                  </Link>
                  <Link
                    to="/services/hubspot"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openServices}
                    onBlur={closeServices}
                  >
                    HubSpot Services
                  </Link>
                  <Link
                    to="/services/custom-apps"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openServices}
                    onBlur={closeServices}
                  >
                    Custom Apps
                  </Link>
                  <Link
                    to="/services/ppc"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openServices}
                    onBlur={closeServices}
                  >
                    PPC Advertising
                  </Link>
                  <Link
                    to="/services/content-marketing"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openServices}
                    onBlur={closeServices}
                  >
                    Content Marketing
                  </Link>
                  <Link
                    to="/services/social-media"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openServices}
                    onBlur={closeServices}
                  >
                    Social Media Marketing
                  </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Markets Dropdown */}
            <div
              className="relative"
              onMouseEnter={openMarkets}
              onMouseLeave={closeMarkets}
            >
              <Link
                to="/markets"
                className="text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors duration-200 flex items-center"
                aria-haspopup="true"
                aria-expanded={marketsDropdownOpen}
                onFocus={openMarkets}
                onBlur={closeMarkets}
              >
                Markets
                <svg className="w-4 h-4 ml-1" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Link>
              {marketsDropdownOpen && (
                <div className="absolute top-full left-0 mt-0 pt-2 w-48 dropdown-enter" role="menu" aria-label="Markets">
                  <div className="bg-white dark:bg-neutral-dark-200 rounded-lg shadow-xl border border-neutral-200 dark:border-neutral-dark-400 py-2">
                  <Link
                    to="/markets/recruitment"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openMarkets}
                    onBlur={closeMarkets}
                  >
                    Recruitment
                  </Link>
                  <Link
                    to="/markets/saas"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openMarkets}
                    onBlur={closeMarkets}
                  >
                    SaaS
                  </Link>
                  <Link
                    to="/markets/hospitality"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openMarkets}
                    onBlur={closeMarkets}
                  >
                    Hospitality
                  </Link>
                  <Link
                    to="/markets/education"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openMarkets}
                    onBlur={closeMarkets}
                  >
                    Education
                  </Link>
                  <Link
                    to="/markets/energy"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openMarkets}
                    onBlur={closeMarkets}
                  >
                    Energy
                  </Link>
                  <Link
                    to="/markets/engineering"
                    role="menuitem"
                    className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-dark-700 hover:bg-neutral-50 dark:hover:bg-neutral-dark-300 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                    onFocus={openMarkets}
                    onBlur={closeMarkets}
                  >
                    Engineering
                  </Link>
                  </div>
                </div>
              )}
            </div>

              <Link
                to="/case-studies"
                className="text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-colors duration-200"
              >
                Case Studies
              </Link>
            </nav>

            {/* Hamburger Menu Button - Mobile only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md bg-neutral-100 dark:bg-neutral-dark-300 hover:bg-neutral-200 dark:hover:bg-neutral-dark-400 transition-all focus:outline-none focus:ring-2 focus:ring-primary-indigo"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {mobileMenuOpen ? (
                <svg
                  className="w-6 h-6 text-neutral-700 dark:text-neutral-dark-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 text-neutral-700 dark:text-neutral-dark-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>

            {/* Dark mode toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-md bg-neutral-100 dark:bg-neutral-dark-300 hover:bg-neutral-200 dark:hover:bg-neutral-dark-400 transition-all focus:outline-none focus:ring-2 focus:ring-primary-indigo"
              aria-label="Toggle dark mode"
            >
              {darkMode ? (
                <svg
                  className="w-5 h-5 text-accent-amber"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 text-neutral-700 dark:text-neutral-dark-700"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>

            {/* Contact Us button - Always visible */}
            {isHomePage ? (
              <a
                href="#contact"
                className="btn-hover px-4 md:px-6 py-2.5 min-h-[44px] bg-primary-rose text-white rounded-lg font-medium text-sm md:text-base hover:bg-primary-rose/90 focus:outline-none focus:ring-2 focus:ring-primary-rose focus:ring-offset-2 transition-all"
              >
                Contact Us
              </a>
            ) : (
              <Link
                to="/contact"
                className="btn-hover px-4 md:px-6 py-2.5 min-h-[44px] bg-primary-rose text-white rounded-lg font-medium text-sm md:text-base hover:bg-primary-rose/90 focus:outline-none focus:ring-2 focus:ring-primary-rose focus:ring-offset-2 transition-all"
              >
                Contact Us
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Menu Dropdown - with smooth slide down animation */}
        <div
          id="mobile-menu"
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
          aria-hidden={!mobileMenuOpen}
        >
          <div className="py-4 border-t border-neutral-200 dark:border-neutral-dark-300">
            <nav className="flex flex-col space-y-4">
              {/* Services Section */}
              <div>
                <Link
                  to="/services"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-xs font-semibold text-neutral-500 dark:text-neutral-dark-500 uppercase mb-2 block hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                >
                  Services
                </Link>
                <div className="flex flex-col space-y-2 pl-2">
                  <Link
                    to="/services/seo"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    Search Engine Optimisation
                  </Link>
                  <Link
                    to="/services/website-development"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    Website Development
                  </Link>
                  <Link
                    to="/services/ai-automation"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    AI & Automation Solutions
                  </Link>
                  <Link
                    to="/services/hubspot"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    HubSpot Services
                  </Link>
                  <Link
                    to="/services/custom-apps"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    Custom Apps
                  </Link>
                  <Link
                    to="/services/ppc"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    PPC Advertising
                  </Link>
                  <Link
                    to="/services/content-marketing"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    Content Marketing
                  </Link>
                  <Link
                    to="/services/social-media"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    Social Media Marketing
                  </Link>
                </div>
              </div>

              {/* Markets Section */}
              <div>
                <Link
                  to="/markets"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-xs font-semibold text-neutral-500 dark:text-neutral-dark-500 uppercase mb-2 block hover:text-primary-indigo dark:hover:text-primary-rose transition-colors"
                >
                  Markets
                </Link>
                <div className="flex flex-col space-y-2 pl-2">
                  <Link
                    to="/markets/recruitment"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    Recruitment
                  </Link>
                  <Link
                    to="/markets/saas"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    SaaS
                  </Link>
                  <Link
                    to="/markets/hospitality"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    Hospitality
                  </Link>
                  <Link
                    to="/markets/education"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    Education
                  </Link>
                  <Link
                    to="/markets/energy"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    Energy
                  </Link>
                  <Link
                    to="/markets/engineering"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-1"
                  >
                    Engineering
                  </Link>
                </div>
              </div>

              <Link
                to="/case-studies"
                onClick={() => setMobileMenuOpen(false)}
                className="text-neutral-700 dark:text-neutral-dark-700 hover:text-primary-indigo dark:hover:text-primary-rose transition-all duration-200 py-2"
              >
                Case Studies
              </Link>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
